import React, {
	ComponentProps,
	forwardRef,
	ReactElement,
} from 'react';
import './InputRadio.css';
import Label from '../Label';
import { IToolTipProps } from '../ToolTip/ToolTip';
import resolvedClassName from '../../utils/resolvedClassName';
import CardContainer from '../CardContainer';
import Row from '../../foundational/Layout/Row';
import Icon from '../Icon';
import correctIcon from '../../assets/icons/icon-correct.svg';
import incorrectIcon from '../../assets/icons/icon-incorrect.svg';

interface IInputRadioProps extends ComponentProps<'input'> {
	className?: string;
	labelClass?: string;
	value: string;
	setValue: (e: string) => void;
	label?: string | ReactElement | ReactElement[];
	name?: string;
	id?: string;
	disabled?: boolean;
	required?: boolean;
	checked?: boolean;
	helperText?: string | ReactElement | ReactElement[];
	tooltip?: string | ReactElement | ReactElement[];
	description?: string | ReactElement | ReactElement[];
	error?: boolean;
	errorText?: string;
	showErrorAndDescription?: boolean;
	tabIndex?: number;
	tooltipNotchPosition?: IToolTipProps['notchPosition'];
	mobileTooltipNotchPosition?: IToolTipProps['notchPosition'];
	status?: undefined | 'correct' | 'incorrect';
	iconPath?: string;
	variant?: 'default' | 'quiz';
}

const InputRadio = forwardRef(
	(
		{
			className = '',
			labelClass = '',
			value = '',
			setValue,
			label = '',
			name = '',
			id = '',
			disabled = false,
			required = false,
			checked = false,
			helperText = '',
			tooltip = '',
			description = '',
			error = false,
			errorText = '',
			showErrorAndDescription = false,
			tabIndex = 0,
			tooltipNotchPosition = 'bottom-left',
			mobileTooltipNotchPosition = 'bottom-right',
			status,
			iconPath,
			variant = 'default',
			...rest
		}: IInputRadioProps,
		ref: any,
	) => {
		const defaultVariant = () => {
			return (
				<>
					<label
						ref={ref}
						htmlFor={id}
						className={resolvedClassName(
							'InputRadio',
							className,
							disabled && 'disabled',
						)}
						onKeyDown={(e) => {
							if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
								setValue(value);
								e.preventDefault();
							}
						}}
						tabIndex={disabled ? -1 : 0}
						>
						<input
							{...rest}
							id={id}
							type='radio'
							className='InputRadio__hidden-input'
							name={name}
							checked={checked}
							value={value}
							disabled={disabled}
							aria-required={required}
							aria-disabled={disabled}
							aria-invalid={error}
							onChange={(e: React.FormEvent<HTMLInputElement>) =>
								setValue(e.currentTarget.value)
							}
							tabIndex={-1}
							/>
						<div className='InputRadio__body' id={id && `${id}Radio`}>
							<div className='InputRadio__radio__container'>
								<span
								onClick={(e) => {
									setValue(value)
									e.preventDefault();
								}
								}
									className={`InputRadio__radio__span ${
										error && !disabled
											? 'InputRadio__radio__span__error'
											: ''
									}`}
								>
								</span>
							</div>
							<Label
								helperText={helperText}
								className={labelClass}
								tooltip={tooltip}
								error={error}
								tooltipAlign={tooltipNotchPosition}
								mobileTooltipAlign={mobileTooltipNotchPosition}
								required={required}
							>
								{label}
							</Label>
						</div>
					</label>
					{showErrorAndDescription ? (
						<>
							{error && errorText && !disabled && (
								<p className='InputRadio__required_error'>
									{errorText}
								</p>
							)}
							{description && (
								<p className='InputRadio__description'>
									{description}
								</p>
							)}
						</>
					) : (
						((error && errorText !== '' && !disabled) ||
							description) && (
							<p
								className={`InputRadio${
									error && errorText && !disabled
										? '__required_error'
										: '__description'
								}`}
							>
								{error && errorText !== '' && !disabled
									? errorText
									: description}
							</p>
						)
					)}
				</>
			);
		};

		const quizVariant = () => {
			const finalStatus = status;
			return (
				<>
					<CardContainer
						className={`InputRadio InputRadio__quiz-container ${finalStatus ? (finalStatus === 'correct' ? 'InputRadio__correct' : 'InputRadio__incorrect') : ''}`}
						borderSettings={checked ? {borderColor: 'blue-creditone'}: {borderColor: 'grey-chalice-50'}}
						onClick={() => !disabled && setValue(value)}
						onKeyDown={(e) => {
							if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
								e.preventDefault();
								setValue(value);
							}
						}}
						tabIndex={disabled ? -1 : 0}
						role="radio"
						aria-checked={checked}>
						<Row justify='start'>
							<label
								ref={ref}
								htmlFor={id}
								className={resolvedClassName(
									'InputRadio',
									className,
									disabled && 'disabled',
								)}>
								<input
									{...rest}
									id={id}
									type='radio'
									className='InputRadio__hidden-input'
									name={name}
									checked={checked}
									value={value}
									disabled={disabled}
									aria-required={required}
									aria-disabled={disabled}
									aria-invalid={error}
									tabIndex={-1}
									onChange={(e: React.FormEvent<HTMLInputElement>) => 
										setValue(e.currentTarget.value)
									} />
								<div className='InputRadio__body'>
									<div className='InputRadio__radio__container'>
										<span
											className={`InputRadio__radio__span ${error && !disabled
												? 'InputRadio__radio__span__error'
												: ''
												} ${finalStatus
												? `InputRadio__radio__span__status`
												: ''}`}
										>
											{finalStatus && (finalStatus === 'correct' ? (
											<Icon customSize={24} src={correctIcon} alt="Correct" />
											) : (
											<Icon customSize={20} src={incorrectIcon} alt="Incorrect" />
											)
											)}
										</span>
									</div>
									{iconPath &&
									<Icon size='utility' src={iconPath} />}
									<Label
										helperText={helperText}
										className={labelClass}
										tooltip={tooltip}
										error={error}
										tooltipAlign={tooltipNotchPosition}
										mobileTooltipAlign={mobileTooltipNotchPosition}
										required={required}>
										{label}
									</Label>
								</div>
							</label>
							{showErrorAndDescription ? (
								<>
									{error && errorText && !disabled && (
										<p className='InputRadio__required_error'>
											{errorText}
										</p>
									)}
									{description && (
										<p className='InputRadio__description'>
											{description}
										</p>
									)}
								</>
							) : (
								((error && errorText !== '' && !disabled) ||
									description) && (
									<p
										className={`InputRadio${error && errorText && !disabled
												? '__required_error'
												: '__description'
											}`}
									>
										{error && errorText !== '' && !disabled
											? errorText
											: description}
									</p>
								)
							)}
						</Row>
					</CardContainer>
				</>
			);
		};

		const getRadioVariant = () => {
			if (variant === 'quiz') {
				return quizVariant();
			}
			return defaultVariant();
		};

		return (
			<>
				{getRadioVariant()}
			</>
		);
	},
);
InputRadio.displayName = 'InputRadio';
export default InputRadio;
